import React, { useContext, useEffect } from 'react';
import { Paragraph } from '@zillow/constellation-10';
import { hasMissingAttachmentContent } from '@zg-rentals/satellite-components';
import { AttachmentsForMessage, logAttachmentsRenderError } from '../..';
import StringConstants from '../../../../constants/StringConstants';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import ExpiredAttachmentsText from '../ExpiredAttachmentsText';
import ImageThumbnail from '../ImageThumbnail';
import { StyledImageThumbnailGrid } from './StyledComponents';

type Props = {
  imageAttachments: AttachmentsForMessage;
  isMessageOwner: boolean;
  onClick(zDocId: string): void;
  overflowCount: number;
};

const LOGGER_NAME = 'MessageAttachments_ImageThumbnailGrid';
const { IMPERSONATION_DETECTED } = StringConstants.ATTACHMENTS;

const ImageThumbnailGrid = ({ imageAttachments, isMessageOwner, onClick, overflowCount }: Props): JSX.Element => {
  const { conversationId } = useContext(InboxPageContext);
  const showOverflowCount = overflowCount > 0;
  const isSingleImage = imageAttachments.length === 1;
  let showExpiredText = false;
  let shouldLogAttachmentsRenderError = false;

  useEffect(() => {
    if (shouldLogAttachmentsRenderError) {
      logAttachmentsRenderError({ conversationId: conversationId || '', loggerName: LOGGER_NAME });
    }
  }, [shouldLogAttachmentsRenderError]);

  return (
    <>
      <StyledImageThumbnailGrid
        display="inline-flex"
        marginBottom={1}
        justifyContent={isMessageOwner ? 'flex-end' : 'flex-start'}
      >
        {imageAttachments.map((imageAttachment, index) => {
          const { attachmentMetadataStatus, fileName, isAttachmentExpired, previewUrl, zDocId } = imageAttachment;

          const hasMissingContent = hasMissingAttachmentContent(attachmentMetadataStatus, isAttachmentExpired);
          const shouldDarkenImage = showOverflowCount && index === 5;
          const imageSize = isSingleImage ? 160 : 94;
          const isImpersonator = previewUrl === IMPERSONATION_DETECTED;
          const shouldShowPlaceholder = !previewUrl || isAttachmentExpired || isImpersonator || hasMissingContent;
          const key = zDocId ? `imageThumbnail-${zDocId}` : `imageThumbnail-${index}`;

          if (!showExpiredText && isAttachmentExpired) showExpiredText = true;

          if (hasMissingContent) shouldLogAttachmentsRenderError = true;

          const handleImageThumbnailClick = () => (zDocId ? onClick(zDocId) : null);

          return (
            <ImageThumbnail
              key={key}
              fileName={fileName}
              isMessageOwner={isMessageOwner}
              onClick={handleImageThumbnailClick}
              shouldDarkenImage={shouldDarkenImage}
              shouldShowExpiredCaption={isSingleImage && isAttachmentExpired}
              shouldShowPlaceholder={shouldShowPlaceholder}
              size={imageSize}
              url={previewUrl || ''}
            />
          );
        })}

        {showOverflowCount && !showExpiredText && (
          <Paragraph
            data-testid="image-overflow-count"
            textStyle="body-lg-bold"
            css={{ color: 'white', position: 'absolute', bottom: 'tighter', right: 'tighter' }}
          >
            {`+${overflowCount}`}
          </Paragraph>
        )}
      </StyledImageThumbnailGrid>

      {showExpiredText && <ExpiredAttachmentsText isPlural={!isSingleImage} />}
    </>
  );
};

export default ImageThumbnailGrid;
